/* @flow */

import {combineReducers} from 'redux';
import startsWith from 'underscore.string/startsWith';
import endsWith from 'underscore.string/endsWith';

import type {JoyrideAction} from './onboarding-actions';
import {
    JOYRIDE_DISABLED,
    JOYRIDE_ENABLED,
    JOYRIDE_RESTARTED,
    JOYRIDE_TEMPORARILY_STOPPED,
    JOYRIDE_ACTIVE_JOYRIDE_STOPPED,
    JOYRIDE_STARTED,
    JOYRIDE_STEP_INDEX_CHANGED,
} from './onboarding-actions';
import {
    NOUNS,
    ENTITY_LIST_LAYOUT_INTRO,
    VISUAL_PIPELINE_INTRO,
    ENTITY_LIST_PLUS_VP_INTRO,
    EMAIL_DRIP_SEQUENCE_TEMPLATES,
    CONSTANT_CONTACT_EXPORT,
} from './onboarding-constants';
import type {JoyrideId} from './onboarding-constants';

// TODO: This should get replaces with the actual state shape. - MLa
export type OnboardingState = Object;

const joyrideIds = [
    NOUNS,
    ENTITY_LIST_LAYOUT_INTRO,
    VISUAL_PIPELINE_INTRO,
    ENTITY_LIST_PLUS_VP_INTRO,
    EMAIL_DRIP_SEQUENCE_TEMPLATES,
    CONSTANT_CONTACT_EXPORT,
];

/*
 * Defaults
 */
const defaultIsRehydrated = false;
const defaultIsEnabled = true;
const defaultIsTemporarilyStopped = false;
const defaultActiveJoyrideId: ?JoyrideId = null;

const defaultJoyrideState = {
    stepIndex: 0,
};

const defaultJoyrides = {
    [NOUNS]: {...defaultJoyrideState},
    [ENTITY_LIST_LAYOUT_INTRO]: {...defaultJoyrideState},
    [VISUAL_PIPELINE_INTRO]: {...defaultJoyrideState},
    [ENTITY_LIST_PLUS_VP_INTRO]: {...defaultJoyrideState},
    [EMAIL_DRIP_SEQUENCE_TEMPLATES]: {...defaultJoyrideState},
    [CONSTANT_CONTACT_EXPORT]: {...defaultJoyrideState},
};

/*
 * Type definitions
 */

type JoyrideState = {
    stepIndex: number,
};

type JoyridesState = {
    [key: JoyrideId]: JoyrideState,
};

/*
 * Reducers
 */

const isRehydratedReducer = (
    state: boolean = defaultIsRehydrated,
    action: JoyrideAction
): boolean => {
    if (action.type === 'persist/REHYDRATE') return true;

    return state;
};

const isEnabledReducer = (state: boolean = defaultIsEnabled, action: JoyrideAction): boolean => {
    switch (action.type) {
        case JOYRIDE_DISABLED: {
            return false;
        }
        case JOYRIDE_ENABLED: {
            return true;
        }
        default:
            return state;
    }
};

const isTemporarilyStoppedReducer = (
    state: boolean = defaultIsTemporarilyStopped,
    action: JoyrideAction
): boolean => {
    switch (action.type) {
        case JOYRIDE_TEMPORARILY_STOPPED: {
            return true;
        }
        case JOYRIDE_STARTED: {
            return false;
        }
        default:
            return state;
    }
};

export const activeJoyrideIdReducer = (
    state: ?JoyrideId = defaultActiveJoyrideId,
    action: JoyrideAction
): ?JoyrideId => {
    switch (action.type) {
        case JOYRIDE_STARTED:
        case JOYRIDE_RESTARTED:
            return action.payload.joyrideId;
        case JOYRIDE_ACTIVE_JOYRIDE_STOPPED:
            return null;
        case 'persist/REHYDRATE': {
            const persistedState = action.payload;
            if (typeof NutClientConfig === 'undefined') {
                if (
                    !persistedState ||
                    !persistedState.onboarding ||
                    !persistedState.onboarding.activeJoyrideId
                ) {
                    return state;
                }

                return persistedState.onboarding.activeJoyrideId;
            }

            return NutClientConfig.onboardingJoyrideActiveId || null;
        }
        default:
            return state;
    }
};

export const joyridesReducer = (
    state: JoyridesState = defaultJoyrides,
    action: JoyrideAction
): JoyridesState => {
    switch (action.type) {
        case JOYRIDE_STEP_INDEX_CHANGED: {
            const {joyrideId, stepIndex} = action.payload;
            if (!joyrideId || isNaN(stepIndex)) return state;
            const newJoyrideState: JoyrideState = {...state[joyrideId], stepIndex: stepIndex};

            return {...state, [joyrideId]: newJoyrideState};
        }
        case JOYRIDE_RESTARTED: {
            const {joyrideId} = action.payload;
            const newJoyrideState: JoyrideState = {...state[joyrideId], stepIndex: 0};

            return {...state, [joyrideId]: newJoyrideState};
        }
        case 'persist/REHYDRATE': {
            const persistedState = action.payload;
            if (typeof NutClientConfig === 'undefined') {
                if (
                    !persistedState ||
                    !persistedState.onboarding ||
                    !persistedState.onboarding.joyrides
                ) {
                    return state;
                }

                return persistedState.onboarding.joyrides;
            }

            const joyrideOnboardingStepIndexKeys = Object.keys(NutClientConfig).filter((key) => {
                return startsWith(key, 'onboardingJoyrideCurrentStepIndex');
            });

            const persistedStepIndexById = {};

            joyrideIds.forEach((joyrideId) => {
                const persistedSettingKey = joyrideOnboardingStepIndexKeys.find((key) => {
                    return endsWith(key, joyrideId);
                });

                persistedStepIndexById[joyrideId] =
                    persistedSettingKey && !isNaN(NutClientConfig[persistedSettingKey])
                        ? {stepIndex: Number(NutClientConfig[persistedSettingKey])}
                        : {stepIndex: 0}; // TODO (@ianvs): Use persisted state, if avaialable
            });

            return {
                ...state,
                ...persistedStepIndexById,
            };
        }
        default:
            return state;
    }
};

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const onboardingReducer = combineReducers({
    isRehydrated: isRehydratedReducer,
    isEnabled: isEnabledReducer,
    isTemporarilyStopped: isTemporarilyStoppedReducer,
    activeJoyrideId: activeJoyrideIdReducer,
    joyrides: joyridesReducer,
});

/**
 * Rehydrate from persisted state, if available.
 *
 * Caveat: this expects only one level of nesting under `onboarding`.
 *
 * @param  {Object} state          - The previous state
 * @param  {Object} persistedState - Persisted state, if available
 * @param  {string} key            - Name of the subreducer
 * @return {Object}                - New state
 */
export function simpleHydrate(state: any, persistedState: Object, key: string): any {
    if (!persistedState || !persistedState.onboarding || !persistedState.onboarding[key])
        return state;

    if (typeof persistedState.onboarding[key] === 'object') {
        return {...state, ...persistedState.onboarding[key]};
    } else {
        return persistedState.onboarding[key];
    }
}
